import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeSlash, ArrowRight, Package, Clock, ShieldCheck } from '@phosphor-icons/react';
import { Button } from '../../components/common';
import { authService } from '../../services';
import './Auth.css';

const REMEMBERED_IDENTIFIER_KEY = 'washx_remembered_identifier';

export function Login() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    identifier: localStorage.getItem(REMEMBERED_IDENTIFIER_KEY) || '',
    password: '',
  });
  const [rememberMe, setRememberMe] = useState(!!localStorage.getItem(REMEMBERED_IDENTIFIER_KEY));
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Re-trigger the error's entrance animation on repeat failures, not just the first one.
  const [errorKey, setErrorKey] = useState(0);

  useEffect(() => {
    if (error) setErrorKey((k) => k + 1);
  }, [error]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authService.login(formData.identifier, formData.password);

      if (response.user) {
        if (rememberMe) {
          localStorage.setItem(REMEMBERED_IDENTIFIER_KEY, formData.identifier);
        } else {
          localStorage.removeItem(REMEMBERED_IDENTIFIER_KEY);
        }

        localStorage.setItem('authToken', 'authenticated');
        localStorage.setItem('user', JSON.stringify(response.user));

        // Small delay to ensure cookie is set before navigation
        await new Promise(resolve => setTimeout(resolve, 100));

        navigate('/', { replace: true });
      } else {
        setError('Login failed - no user data received');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-brand-panel">
        <div className="auth-brand-content">
          <img src="/logo.svg" alt="WashX" className="auth-brand-logo" />
          <p className="auth-brand-tagline">Laundry Operations Portal</p>

          <div className="auth-illustration-wrap">
            <img
              src="/login-illustration.jpg"
              alt=""
              className="auth-illustration-img"
            />
          </div>

          <ul className="auth-brand-points">
            <li>
              <Package size={20} weight="bold" />
              <span>Track every order from pickup to delivery</span>
            </li>
            <li>
              <Clock size={20} weight="bold" />
              <span>Live pickup and delivery slot scheduling</span>
            </li>
            <li>
              <ShieldCheck size={20} weight="bold" />
              <span>Built for fast, accurate front-desk work</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="auth-form-panel">
        <div className="auth-card">
          <img src="/logo.svg" alt="WashX" className="auth-card-logo" />

          <div className="auth-header">
            <h1 className="auth-title">Sign in</h1>
            <p className="auth-subtitle">Enter your credentials to access the portal</p>
          </div>

          <form className="auth-form" onSubmit={handleSubmit}>
            {error && (
              <div className="auth-error" role="alert" key={errorKey}>
                {error}
              </div>
            )}

            <div className="form-group">
              <label className="form-label" htmlFor="login-identifier">Phone Number or Email</label>
              <input
                id="login-identifier"
                type="text"
                className="form-input"
                placeholder="Enter phone number or email"
                value={formData.identifier}
                onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
                autoComplete="username"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="login-password">Password</label>
              <div className="password-input-wrapper">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  className="form-input"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="form-options">
              <label className="remember-me">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span>Remember my username</span>
              </label>
            </div>

            <Button
              type="submit"
              fullWidth
              loading={loading}
              icon={<ArrowRight size={18} />}
            >
              Sign In
            </Button>
          </form>

          <p className="auth-footer-note">
            Locked out or need access? Contact your admin.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
